import { byId } from './domUtils';
import { hideLoginState } from './authGateController';
import { closeModal, openModal, showView } from './navigationController';
import { recordRuntimeError } from './prototypeRuntimeSupport';
import { setLoginPrompt } from './presentAccessDecision';
import { showAuthModalIntentNote } from './formProfileController';
import type { PrototypeState } from './prototypeHydration';
import type { PlanCode, Root } from './prototypeTypes';
import { replayPendingCheckoutIntent } from './pendingCheckoutIntent';
import { capturePasswordResetToken, clearSensitiveAuthQuery } from './authTokenUrl';
import { validateCapturedPasswordResetToken } from './resetPasswordPreflight';
import { parseClientPlanContext } from './clientPlanContext';
import { parseCanonicalAuthenticatedSession } from './authenticatedSessionContract';

const OAUTH_ERRORS: Record<string, string> = {
	invalid_state: 'Login link expired. Please try again.',
	missing_code: 'Login was not completed. Please try again.',
	token_failed: 'Login verification failed. Please retry.',
	userinfo_failed: 'Could not retrieve account info. Please retry.',
	profile_incomplete: 'Unable to get profile details. Please retry.',
	email_not_verified: 'Email not verified. Please verify your email in Google first.',
	account_conflict: 'This Google account is already linked to another DeepMox account. Contact support if you need help.',
	server_error: 'Service temporarily unavailable. Please try again later.',
};

const OAUTH_TERMS_NOTICE_COOKIE = 'dm_oauth_terms_notice';

function hasCookie(name: string): boolean {
	return document.cookie.split(';').some((part) => part.trim() === `${name}=1`);
}

function clearCookie(name: string): void {
	document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
}

function showOAuthTermsSignup(root: Root): void {
	openModal(root, 'registerModal');
	showAuthModalIntentNote(root, 'register', 'Please continue with Google to create your account.');
	const alert = byId(root, 'registerFormAlert');
	if (alert) {
		alert.textContent = 'Google sign-in needs to be restarted. Select Continue with Google to create your account.';
		alert.hidden = false;
	}
}

export function handlePrototypeDeepLinks(root: Root, state: PrototypeState): void {
	if (typeof window === 'undefined') return;
	const params = new URLSearchParams(window.location.search);
	if (hasCookie(OAUTH_TERMS_NOTICE_COOKIE)) {
		clearCookie(OAUTH_TERMS_NOTICE_COOKIE);
		showOAuthTermsSignup(root);
	}
	const oauthError = params.get('oauth_error');
	if (oauthError) {
		if (oauthError === 'terms_required') {
			// Compatibility for an OAuth callback from a Worker version deployed
			// before the clean flash-cookie handoff above.
			showOAuthTermsSignup(root);
		} else {
			openModal(root, 'loginModal');
			setLoginPrompt(root, OAUTH_ERRORS[oauthError] ?? OAUTH_ERRORS.server_error);
		}
		const cleanUrl = new URL(window.location.href);
		cleanUrl.searchParams.delete('oauth_error');
		window.history.replaceState({}, '', cleanUrl.toString());
	}
	if (params.get('oauth_link') === 'required') {
		openModal(root, 'oauthLinkModal');
		clearSensitiveAuthQuery();
	}

	const pathname = window.location.pathname.replace(/^\/+|\/+$/g, '');
	if (pathname === 'verify-email') void handleVerifyEmailDeepLink(root, state);
	if (pathname === 'reset-password') handleResetPasswordDeepLink(root, state);
}

async function handleVerifyEmailDeepLink(root: Root, state: PrototypeState): Promise<void> {
	const token = new URLSearchParams(window.location.search).get('token');
	openModal(root, 'verifyEmailOverlay');
	if (!token) return showErrorState(root, 'verifyEmailError', 'verifyEmailLoading');
	clearSensitiveAuthQuery();
	try {
		const verification = await state.runtime.api.verifyEmail({ token }) as { sessionIssued?: boolean };
		const loading = byId(root, 'verifyEmailLoading');
		const success = byId(root, 'verifyEmailSuccess');
		if (loading) loading.style.display = 'none';
		if (success) success.style.display = 'block';
		if (verification.sessionIssued !== true) {
			const detail = success?.querySelector<HTMLElement>('.forgot-success-detail');
			if (detail) detail.textContent = 'Your email is verified. Please sign in to continue.';
			setTimeout(() => {
				closeModal(root, 'verifyEmailOverlay');
				showView('home', root);
				window.history.replaceState({}, '', '/');
				openModal(root, 'loginModal');
				setLoginPrompt(root, 'Your email is verified. Please sign in to continue.');
			}, 2000);
			return;
		}
		await hydrateVerifiedSession(root, state);
		if (state.isLoggedIn) {
			await replayPendingCheckoutIntent(state.runtime.userId, async (plan, billingCycle, source) => {
				const { previewUpgrade } = await import('./commerceController');
				const { failValidation } = await import('./prototypeRuntimeSupport');
				const outcome = await previewUpgrade(plan, root, state, recordRuntimeError, { source, billingCycle });
				if (!outcome.ok && 'message' in outcome) failValidation(outcome.message);
			});
		}
		setTimeout(() => {
			closeModal(root, 'verifyEmailOverlay');
			showView('home', root);
			window.history.replaceState({}, '', '/');
		}, 2000);
	} catch (error) {
		recordRuntimeError(state.runtime, error);
		showErrorState(root, 'verifyEmailError', 'verifyEmailLoading');
	}
}

async function hydrateVerifiedSession(root: Root, state: PrototypeState): Promise<void> {
	try {
		const session = parseCanonicalAuthenticatedSession(await state.runtime.api.getSession());
		if (!session) throw new Error('verified_session_incomplete');
		state.isLoggedIn = true;
		state.runtime.api.setAuthenticated(true);
		const planContext = parseClientPlanContext(session.viewer.planContext);
		state.planContext = planContext;
		state.currentPlan = planContext?.billingPlan ?? 'free';
		state.effectiveAccessPlan = planContext?.effectiveAccessPlan ?? 'free';
		state.runtime.currentPlan = state.effectiveAccessPlan;
		state.runtime.userId = session.viewer.userId;
		state.runtime.sessionHash = session.viewer.sessionHash;
		if (session.viewer.email) state.runtime.userEmail = session.viewer.email;
		if (session.viewer.displayName) state.runtime.userDisplayName = session.viewer.displayName;
		hideLoginState(root, state);
	} catch (error) {
		recordRuntimeError(state.runtime, error);
	}
}

function handleResetPasswordDeepLink(root: Root, state: PrototypeState): void {
	const token = capturePasswordResetToken();
	openModal(root, 'resetPasswordOverlay');
	if (token) {
		void validateCapturedPasswordResetToken(root, state);
		return;
	}
	const form = byId(root, 'resetPasswordForm');
	const error = byId(root, 'resetPasswordError');
	if (form) form.style.display = 'none';
	if (error) error.style.display = 'block';
}

function showErrorState(root: Root, errorId: string, loadingId: string): void {
	const loading = byId(root, loadingId);
	const error = byId(root, errorId);
	if (loading) loading.style.display = 'none';
	if (error) error.style.display = 'block';
}
